<template>
  <div>
    <SoDataTable
      ref="transactions-table"
      v-model:filters="tableFilters"
      :headers="tableHeaders"
      :items="tableItems"
      :loading="isTableLoading"
      :has-pagination="false"
      show-copy-to-clipboard
      class="transactions-table"
      :searchable="false"
      row-clickable
      @click:row="onRowClick"
    >
      <template #tableActions>
        <div>
          <SoTooltip
            :disabled="hasBills"
            text="There are no accepted bills on this account"
          >
            <SoButton
              :disabled="billsZipLink?.isFetching || !hasBills"
              :loading="billsZipLink?.isFetching"
              title="Download All Bills"
              download
              class="mr-2"
              prepend-icon="download"
              data-testid="transactions-download-all-bills"
              large
              @click="downloadBills"
            />
          </SoTooltip>
          <SoButton
            class="mr-2"
            title="Issue New Bill"
            prepend-icon="plus"
            data-testid="transactions-issue-new-bill"
            large
            @click="
              $router.push({
                name: RouteNames.CustomerIssueBill,
                params: { accountNumber: current.accountNumber },
              })
            "
          />
          <SoTooltip
            :disabled="!getAccountCreditTooltip()"
            :text="getAccountCreditTooltip()"
            location="start"
          >
            <SoButton
              title="Account Credit"
              prepend-icon="plus"
              data-testid="transactions-account-credit"
              large
              :disabled="accountCreditButtonIsDisabled()"
              @click="showCreditModal = true"
            />
          </SoTooltip>
        </div>
      </template>
      <template #item.billNumber="{ internalItem }">
        <SoTooltip
          :disabled="!internalItem.raw.isFailed"
          text="Failed Bill"
          location="start"
        >
          <div
            class="d-flex align-center text-no-wrap"
            :class="{
              'is-failed': internalItem.raw.isFailed,
            }"
          >
            <SoIcon
              :icon="
                internalItem.raw.isFailed
                  ? 'close-circle'
                  : internalItem.raw.icon
              "
              :class="{ 'text-danger': internalItem.raw.isFailed }"
              size="small"
              class="mr-2"
            />
            <span class="text-primary text-decoration-none">
              {{ internalItem.raw.billNumber }}
            </span>
          </div>
        </SoTooltip>
      </template>
      <template #item.title="{ internalItem }">
        <SoTooltip
          :disabled="!internalItem.raw.isFailed"
          :text="`Failed ${internalItem.raw.type}`"
          location="start"
        >
          <div
            class="d-flex align-center text-no-wrap"
            :class="{
              'is-failed': internalItem.raw.isFailed,
            }"
          >
            <SoIcon :icon="internalItem.raw.icon" size="small" class="mr-2" />
            <span class="text-primary text-decoration-none">
              {{ internalItem.raw.title }}
            </span>
          </div>
        </SoTooltip>
      </template>
    </SoDataTable>
    <AccountCreditFormModal
      v-model="showCreditModal"
      @close-modal="handleCloseCreditModal()"
      @submitted="current.accountCreditEligibility.refetch()"
    />
    <BillModal
      v-if="!!billIdToOpen"
      v-model="showBillModal"
      :bill-id="billIdToOpen"
      @close-modal="billIdToOpen = null"
    />
    <FailedBillRequestModal
      v-if="billRequestIdToOpen"
      v-model="showFailedBillRequestModal"
      :bill-request-id="billRequestIdToOpen"
      @close-modal="billRequestIdToOpen = null"
    />
    <TransactionModal
      v-if="transactionIdToOpen"
      v-model="showTransactionModal"
      :transaction-id="transactionIdToOpen"
      @close-modal="billIdToOpen = null"
    />
    <AccountCreditModal
      v-if="showAccountCreditModal && transactionIdToOpen"
      v-model="showAccountCreditModal"
      :transaction-id="transactionIdToOpen"
      :account-credit-id="accountCreditIdToOpen"
      @close-modal="showAccountCreditModal = false"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import BillModal from "../modals/BillModal.vue"
  import TransactionModal from "../modals/TransactionModal.vue"
  import AccountCreditModal from "../modals/AccountCreditModal.vue"
  import FailedBillRequestModal from "../modals/FailedBillRequestModal.vue"
  import AccountCreditFormModal from "../modals/AccountCreditFormModal.vue"
  import {
    SoDataTableCustomComponent,
    SoDataTableHeader,
    FilterType,
  } from "@/types/components/SoDataTable"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { RouteNames } from "@/types/router"
  import { formatDatePeriod } from "@/helpers/dateHelper"
  import { Status } from "@/types/Status"
  import { IBillRequest } from "@/models/billRequest/billRequest.types"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { ITransaction } from "@/models/transaction/transaction.types"
  import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
  import { queryClient } from "@/api/utils/useQuery"
  import { BILL_REQUESTS_POLLING_TIME } from "@/constants/general"
  import {
    transactionTypeIcons,
    tableFilters,
    transactionHeaders,
    billTypeStatuses,
    TransactionTableItem,
  } from "@/types/Transactions"
  import { errorReporter } from "@/services/ErrorService"
  import { BillService } from "@/services/BillService"
  import { QueryResult } from "@/api/utils/types"
  import { useSnackbarStore } from "@/stores/snackbar"

  export default defineComponent({
    components: {
      SoIcon,
      SoButton,
      SoDataTable,
      BillModal,
      TransactionModal,
      FailedBillRequestModal,
      SoTooltip,
      AccountCreditFormModal,
      AccountCreditModal,
    },
    data() {
      return {
        snackbar: useSnackbarStore(),
        showCreditModal: false,
        showBillModal: false,
        showFailedBillRequestModal: false,
        showTransactionModal: false,
        showAccountCreditModal: false,
        current: useCurrentAccountStore(),
        billIdToOpen: null as number | null,
        RouteNames,
        transactionIdToOpen: null as number | null,
        accountCreditIdToOpen: "" as string | undefined,
        billRequestIdToOpen: null as number | null,
        tableFilters,
        pollingInterval: null as null | ReturnType<typeof setInterval>,
        billsZipLink: null as null | QueryResult<string>,
      }
    },

    computed: {
      hasBills(): boolean {
        return this.current.bills.data.length > 0
      },
      // Bills and bill requests only fetch once coreId has resolved, so
      // transactions can arrive first; joining against still-empty bills would
      // briefly render every bill row as failed. Hold the table in its loading
      // state until the initial fetch of all three has completed (isFetched
      // stays true during background polls, so polling doesn't flicker this).
      isTableLoading(): boolean {
        if (!this.current.transactions.isFetched) return true
        if (!this.current.coreId.isFetched) return true
        if (!this.current.coreId.data) return false
        return (
          !this.current.bills.isFetched || !this.current.billRequest.isFetched
        )
      },
      tableHeaders(): SoDataTableHeader[] {
        return transactionHeaders.filter((header) => {
          if (!header.showIf) return true
          const filter = this.tableFilters.transactionType
          if (filter.type !== FilterType.CHECKBOX) return true
          return header.showIf(filter.value ?? [])
        })
      },
      billRequests() {
        return this.current.billRequest.data
      },
      failedBillRequests() {
        return this.billRequests
          .filter((billReq) => billReq.isFailed)
          .map((billReq) => ({
            isFailed: true,
            date: billReq.taskCreatedDttm,
            billType: this.getBillTypeStatus(billReq),
            billNumber: billReq.id.toString(),
            icon: this.getTransactionIcon("Bill"),
            title: "Bill",
            type: "Bill",
            billPeriod: {
              datePeriod: {
                from: billReq.billPeriodFromDttm,
                to: billReq.billPeriodToDttm,
              },
            },
            status: {
              value: "Failed",
              props: { status: new Status("critical", "Failed") },
            },
          }))
      },
      transactions() {
        return this.current.transactions.data.map((transaction) => {
          const amount = transaction.changeAmount
          // transaction.billId is the be-financials BILL.ID, joined server-side
          // from BILL.BILLING_ACCOUNT_TRANSACTION_ID — the real foreign key.
          // A bill-number match against transaction.reference cannot be used:
          // Junifer masks reference in UAT, so it is *REDACTED* on staging.
          const bill = transaction.billId
            ? this.current.bills.data.find(
                (bill) => bill.id === transaction.billId,
              )
            : null
          // coreBillId is the be-financials id (SO-32776); billRequest.billId
          // stays Junifer-scoped and cannot be compared to transaction.billId.
          const billRequest = transaction.billId
            ? this.current.billRequest.data.find(
                (billReq) => billReq.coreBillId === transaction.billId,
              )
            : null
          // Backoffice refuses the single-bill fetch for soft-deleted bills,
          // so their rows render bill details but don't open the bill modal.
          let openableBillId = billRequest?.id
          if (bill) {
            openableBillId = bill.isDeleted ? undefined : bill.id
          }
          const billNumber =
            bill?.juniferBillNumber ?? billRequest?.id.toString()
          let description
          if (transaction.description) {
            description = transaction.description
          } else if (bill?.periodFrom && bill?.periodTo) {
            description = formatDatePeriod(bill.periodFrom, bill.periodTo)
          }
          return {
            isFailed: bill === undefined || transaction.isRejected,
            transactionId: transaction.id,
            title: this.getTransactionTitle(transaction, billNumber),
            description: description,
            type: transaction.transactionType,
            balance: transaction.accountBalance,
            billNumber,
            billId: openableBillId,
            date:
              bill?.billAcceptedDate ??
              bill?.billCreatedDate ??
              transaction.acceptedDttm ??
              transaction.createdDttm,
            billPeriod:
              bill?.periodFrom && bill?.periodTo
                ? { datePeriod: { from: bill.periodFrom, to: bill.periodTo } }
                : undefined,
            charge: bill?.charges,
            billType: billRequest
              ? this.getBillTypeStatus(billRequest)
              : undefined,
            icon: this.getTransactionIcon(transaction.transactionType),
            debit: amount < 0 ? -amount : undefined,
            credit: amount > 0 ? amount : undefined,
            creditDebit: -amount,
            status: {
              value: transaction.statusText,
              props: { status: transaction.status },
            },
            accountCreditId: transaction.accountCreditId,
          }
        })
      },
      tableItems() {
        return [...this.failedBillRequests, ...this.transactions].sort(
          (a, b) => (a.date.isBefore(b.date) ? 1 : -1),
        )
      },
      accountCreditEligibility() {
        return this.current.accountCreditEligibility.data
      },
    },
    watch: {
      "billsZipLink.data"(link?: string | null) {
        if (link) {
          window.open(link)
        }
      },
    },
    created() {
      this.pollingInterval = setInterval(
        () => this.fetchBills(),
        BILL_REQUESTS_POLLING_TIME,
      )
    },
    beforeUnmount() {
      if (this.pollingInterval) clearInterval(this.pollingInterval)
    },
    methods: {
      async downloadBills() {
        try {
          if (this.billsZipLink === null) {
            this.billsZipLink =
              await BillService.getBillsZipLinkByBillingAccountId(
                this.current.coreId.data ?? 0,
              )
          } else {
            this.billsZipLink?.refetch()
          }
        } catch (error) {
          errorReporter.report(error)
          console.error(error)
          this.snackbar.addError("Failed to download all bills zip")
        }
      },
      onRowClick(item: TransactionTableItem) {
        if (item.type === "Bill" && item.isFailed) {
          // A bill transaction that matched neither a bill nor a bill request
          // has nothing to open.
          const billRequestId = Number(item.billNumber)
          if (!Number.isNaN(billRequestId)) {
            this.billRequestIdToOpen = billRequestId
            this.showFailedBillRequestModal = true
          }
          return
        }
        if (item.type === "Bill" && item.billId) {
          this.billIdToOpen = item.billId
          this.showBillModal = true
          return
        }
        if (item.transactionId && item.type !== "Account Credit") {
          this.transactionIdToOpen = item.transactionId
          this.showTransactionModal = true
        }
        if (item.transactionId && item.type === "Account Credit") {
          this.transactionIdToOpen = item.transactionId
          this.accountCreditIdToOpen = item.accountCreditId
          this.showAccountCreditModal = true
        }
      },
      getTransactionIcon(transactionType: string): string {
        return transactionTypeIcons[transactionType] ?? "file-question"
      },
      getBillTypeStatus(
        billRequest?: IBillRequest,
      ): SoDataTableCustomComponent | undefined {
        if (!billRequest) return
        const statusType = billTypeStatuses[billRequest.typeCode]
        return {
          value: billRequest.typeCode,
          props: {
            text: billRequest.typeCode,
            type: statusType,
          },
        }
      },
      getTransactionTitle(transaction: ITransaction, billNumber?: string) {
        if (transaction.isFriendReferralCredit) {
          return "Refer-A-Friend Credit"
        }

        if (transaction.isRefund) {
          return "Refund"
        }

        if (transaction.isBill) {
          // Title on the bill NUMBER, which is what Junifer knows the bill as
          // and what agents cross-reference. transaction.billId is a
          // be-financials surrogate key (SO-31069) and means nothing outside
          // Nova, so it must never appear here. Unmatched bills fall back to a
          // bare "Bill", matching the failed-bill-request rows above.
          return billNumber ? `Bill - ${billNumber}` : "Bill"
        }

        return transaction.transactionType
      },
      fetchBills() {
        if (!this.showFailedBillRequestModal && this.current.coreId.data) {
          const billRequests = BackofficeRestQueries.billing.billRequests(
            this.current.coreId.data,
          ).queryKey
          queryClient.invalidateQueries({ queryKey: billRequests })
        }
      },
      handleCloseCreditModal() {
        const transactionRequests = BackofficeRestQueries.billing.transactions(
          this.current.coreId.data ?? 0,
        ).queryKey
        queryClient.invalidateQueries({ queryKey: transactionRequests })
      },
      accountHasComplaints() {
        return this.accountCreditEligibility?.hasOpenComplaints
      },
      accountHasComplaintWithExistingGoodwill() {
        return this.accountCreditEligibility
          ?.hasCreditRelatedToAllOpenComplaints
      },
      accountCreditButtonIsDisabled() {
        return (
          !this.accountHasComplaints() ||
          this.accountHasComplaintWithExistingGoodwill()
        )
      },
      getAccountCreditTooltip() {
        if (!this.accountHasComplaints()) {
          return "You are only eligible to award a Goodwill credit when there is an open complaint"
        }
        if (this.accountHasComplaintWithExistingGoodwill()) {
          return "There has already been a Goodwill credit transaction on this account for this complaint"
        }
        return ""
      },
    },
  })
</script>

<style scoped>
  :deep(.v-data-table__tr:has(.is-failed) td) {
    background-color: rgb(var(--v-theme-danger), 0.1) !important;
    text-decoration: line-through;
  }

  :deep(.v-data-table__tr:has(.is-failed):hover td) {
    background-color: rgb(var(--v-theme-danger), 0.2) !important;
    text-decoration: line-through;
  }
</style>
